import React, { useMemo } from 'react';
import { classNames } from '@wajeez-web/core/utils';
import {
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
} from 'react-icons/md';

export interface UsePaginationProps {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  siblingCount?: number;
}

export interface PaginationProps extends UsePaginationProps {
  onPageChange: (page: number | string) => void;
}

const range = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, idx) => idx + start);
};

export const DOTS = '...';

export const usePagination = ({
  currentPage,
  pageSize,
  siblingCount = 1,
  totalCount,
}: UsePaginationProps) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);

    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;

    /*
          Case 1:
          If the number of pages is less than the page numbers we want to show in our
          paginationComponent, we return the range [1..totalPageCount]
        */
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    /*
            Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
        */
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    /*
          We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
        */
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    /*
            Case 2: No left dots to show, but rights dots to be shown
        */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    /*
            Case 3: No right dots to show, but left dots to be shown
        */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      );
      return [firstPageIndex, DOTS, ...rightRange];
    }

    /*
            Case 4: Both left and right dots to be shown
        */
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    return [];
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
};

const paginationItem =
  'px-3 h-8 text-center my-auto mx-1 opacity-75 flex box-border items-center tracking-wide rounded-2xl leading-6 text-sm min-w-32 cursor-pointer';
const disabledItem = 'pointer-events-none';

const Pagination: React.FC<PaginationProps> = ({
  onPageChange,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
}) => {
  const paginationRange: (number | string)[] =
    usePagination({
      currentPage,
      totalCount,
      siblingCount,
      pageSize,
    }) || [];

  //   if (currentPage === 0 || (paginationRange &&paginationRange.length < 2)) {
  //     return null;
  //   }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  const lastPage = paginationRange[paginationRange.length - 1];
  return (
    <ul className="flex list-none justify-center">
      <li
        className={classNames(
          paginationItem,
          'bg-transparent cursor-pointer ',
          currentPage === 1 ? disabledItem : ''
        )}
        onClick={onPrevious}
      >
        <div className="  ">
          <MdOutlineKeyboardArrowLeft className="h-5 w-5" />
        </div>
      </li>
      {paginationRange.map((pageNumber) => {
        if (pageNumber === DOTS) {
          return (
            <li
              key={pageNumber}
              className={`${paginationItem} hover:bg-transparent cursor-default`}
            >
              &#8230;
            </li>
          );
        }

        return (
          <li
            key={pageNumber}
            className={classNames(
              paginationItem,
              'hover:bg-gray-200/60 ',
              pageNumber === currentPage ? 'bg-ring-color' : ''
            )}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber}
          </li>
        );
      })}
      <li
        className={classNames(
          paginationItem,
          'bg-transparent cursor-pointer',
          currentPage === lastPage ? 'pointer-events-none' : ''
        )}
        onClick={onNext}
      >
        <div className="">
          <MdOutlineKeyboardArrowRight className="h-5 w-5 " />
        </div>
      </li>
    </ul>
  );
};

export default Pagination;
